// Opening move: StressInteractionPlugin - Handles context menu interactions for stress overrides
// Epic 07: Context menu triggering, native menu suppression, command dispatching
// Big play: Single menu instance, proper lifecycle management, STRESS_OVERLAY_TAG for updates

import { useEffect, useState, useCallback } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  FloatingStressMenu,
  type SyllableTarget,
} from '../ui/FloatingStressMenu'
import {
  CYCLE_STRESS_LEVEL_COMMAND,
  SET_STRESS_LEVEL_COMMAND,
  APPLY_STRESS_PATTERN_COMMAND,
  CLEAR_STRESS_OVERRIDE_COMMAND,
  STRESS_OVERLAY_TAG,
} from './StressDataPlugin'

// ============================================================================
// Types
// ============================================================================

interface MenuState {
  target: SyllableTarget
  referenceElement: HTMLElement
}

// ============================================================================
// Plugin Component
// ============================================================================

/**
 * StressInteractionPlugin - Handles context menu interactions for stress overrides
 *
 * Main play: Listen for right-click on syllable overlays, show FloatingStressMenu
 * Big play: Suppress native browser context menu at editor root level
 * Time to tackle the tricky bit: Single menu instance, lifecycle management, command dispatching with STRESS_OVERLAY_TAG
 *
 * Epic 07 Requirements:
 * - Right-click on syllable overlays opens custom menu
 * - Suppress native context menu (Chromium/Firefox/Safari)
 * - Extract syllable data from data attributes (nodeKey, word, syllableIndex, level)
 * - Dispatch commands (CYCLE, SET_LEVEL, APPLY_PATTERN, CLEAR_OVERRIDE)
 * - Tag updates with STRESS_OVERLAY_TAG for immediate overlay refresh
 * - Only one menu instance at a time
 * - Menu state never enters EditorState/serialization
 *
 * Reference: Epic 01 affordances pattern for floating UI within editor container
 */
export default function StressInteractionPlugin() {
  const [editor] = useLexicalComposerContext()
  const [menuState, setMenuState] = useState<MenuState | null>(null)

  // Opening move: Suppress native context menu at editor container level
  // CRITICAL: Use registerRootListener for proper timing - getRootElement() may be null initially
  // Reference: https://lexical.dev/docs/concepts/listeners - "registerRootListener vs getRootElement"
  // Time to tackle the tricky bit: Stress marks are in .stress-overlay-layer (sibling to contenteditable),
  // so we must listen on the parent .baseline-editor-container, not just rootElement
  useEffect(() => {
    // Big play: Define handler outside registerRootListener so we have stable reference for cleanup
    const handleContextMenu = (event: MouseEvent) => {
      // Never allow native menu inside editor; always handle ourselves
      event.preventDefault()
      event.stopPropagation()

      // Hit-test stress marks manually (pointer-events are disabled)
      const marks = Array.from(document.querySelectorAll<HTMLElement>('.stress-mark'))
      const mark = marks.find((el) => {
        const rect = el.getBoundingClientRect()
        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        )
      })

      if (!mark) {
        setMenuState(null)
        return
      }

      const nodeKey = mark.getAttribute('data-syllable-nodekey')
      const word = mark.getAttribute('data-syllable-word')
      const syllableIndex = mark.getAttribute('data-syllable-index')
      const currentLevel = mark.getAttribute('data-syllable-level')

      if (!nodeKey || !word || syllableIndex === null || currentLevel === null) {
        console.warn('StressInteractionPlugin: Missing data attributes on stress mark')
        return
      }

      setMenuState({
        target: {
          nodeKey,
          word,
          syllableIndex: parseInt(syllableIndex, 10),
          currentLevel: parseInt(currentLevel, 10),
        },
        referenceElement: mark,
      })
    }

    // Victory lap: Use registerRootListener for proper timing when root element becomes available
    // This handles cases where getRootElement() would return null during initial render
    return editor.registerRootListener((rootElement, prevRootElement) => {
      // Clean up old listener if root element changed
      if (prevRootElement !== null) {
        const prevContainer = prevRootElement.closest('.baseline-editor-container') as HTMLElement | null
        if (prevContainer) {
          prevContainer.removeEventListener('contextmenu', handleContextMenu)
        }
      }

      // Attach new listener when root element is available
      if (rootElement !== null) {
        const editorContainer = rootElement.closest('.baseline-editor-container') as HTMLElement | null
        if (editorContainer) {
          editorContainer.addEventListener('contextmenu', handleContextMenu)
        } else {
          console.warn('StressInteractionPlugin: Could not find .baseline-editor-container')
        }
      }
    })
  }, [editor])

  // Main play: Close menu handler
  const handleClose = useCallback(() => {
    setMenuState(null)
  }, [])

  // Time to tackle the tricky bit: Command dispatchers with STRESS_OVERLAY_TAG and discrete: true
  const handleCycleLevel = useCallback(
    (target: SyllableTarget) => {
      editor.update(
        () => {
          editor.dispatchCommand(CYCLE_STRESS_LEVEL_COMMAND, {
            nodeKey: target.nodeKey,
            word: target.word,
            syllableIndex: target.syllableIndex,
          })
        },
        { tag: STRESS_OVERLAY_TAG, discrete: true } // CRITICAL: discrete: true makes it undoable
      )
    },
    [editor]
  )

  const handleSetLevel = useCallback(
    (target: SyllableTarget, level: 0 | 1 | 2) => {
      editor.update(
        () => {
          editor.dispatchCommand(SET_STRESS_LEVEL_COMMAND, {
            nodeKey: target.nodeKey,
            word: target.word,
            syllableIndex: target.syllableIndex,
            level,
          })
        },
        { tag: STRESS_OVERLAY_TAG, discrete: true } // CRITICAL: discrete: true makes it undoable
      )
    },
    [editor]
  )

  const handleApplyPattern = useCallback(
    (target: SyllableTarget, pattern: 'trochee' | 'iamb' | 'dactyl' | 'anapest') => {
      editor.update(
        () => {
          editor.dispatchCommand(APPLY_STRESS_PATTERN_COMMAND, {
            nodeKey: target.nodeKey,
            word: target.word,
            pattern,
          })
        },
        { tag: STRESS_OVERLAY_TAG, discrete: true } // CRITICAL: discrete: true makes it undoable
      )
    },
    [editor]
  )

  const handleClearOverrides = useCallback(
    (target: SyllableTarget) => {
      editor.update(
        () => {
          editor.dispatchCommand(CLEAR_STRESS_OVERRIDE_COMMAND, {
            nodeKey: target.nodeKey,
            word: target.word,
          })
        },
        { tag: STRESS_OVERLAY_TAG, discrete: true } // CRITICAL: discrete: true makes it undoable
      )
    },
    [editor]
  )

  // Victory lap: Render menu if state exists
  if (!menuState) {
    return null
  }

  // Big play: Get editor container for portal target
  const rootElement = editor.getRootElement()
  const editorContainer = rootElement?.closest('.baseline-editor-container') as HTMLElement

  if (!editorContainer) {
    console.warn('StressInteractionPlugin: Could not find editor container for portal')
    return null
  }

  return (
    <FloatingStressMenu
      editor={editor}
      target={menuState.target}
      referenceElement={menuState.referenceElement}
      editorContainer={editorContainer}
      onClose={handleClose}
      onCycleLevel={handleCycleLevel}
      onSetLevel={handleSetLevel}
      onApplyPattern={handleApplyPattern}
      onClearOverrides={handleClearOverrides}
    />
  )
}

// Victory lap: StressInteractionPlugin complete with context menu handling
// Native menu suppression, command dispatching with STRESS_OVERLAY_TAG, single menu instance
// Ready for Editor.tsx registration
